import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { BuildingParamsDto } from "@common/dto/buildingSearchRequest.dto";
import { CreateFeasibilityProjectDto } from '@common/dto/feasibilityProjectRequest.dto';
import { PopsParamsDto } from '@common/dto/popsSearchRequest.dto';
import { PointInsideMunicipalityDto } from '@common/dto/pointInsideMunicipality.dto';
import {
  IID_FEASABILITY_REQUEST_IDENTIFIER,
  IID_FEASABILITY_RESPONSE,
  IIDRequestIdentifierBuilding,
  IIDRequestIdentifierNodeType,
  IIDRequestIdentifierPop,
  IIDRequestIdentifierSplice
} from "@common/interfaces/interfaceData";
import { Topics } from '@common/interfaces/topics';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ActionService } from '../action/action.service';
import { CrudService } from '../crudService';
import { ProjectEntityStoreStrategy } from '../updateStoreStrategies';
import { StudioFattibilitaRequestQuery } from './studioFattibilitaRequest.query';
import { StudioFattibilitaRequestStore } from './studioFattibilitaRequest.store';
import { DefaultOption } from '@ep-om/jira-control/om-select/om-select.component';
import { catchError, debounceTime, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { IIDFeasabilityResponseService } from '../iidFeasabilityResponse/iidFeasabilityResponse.service';
import { InterfaceName } from '@common/interfaces/issueTypeInterface';
import { InterfaceDataService } from '../interfaceDataRegistry';

export type NodeTypes = 'building' | 'pop' | 'splice';

export type SpliceSearchDto = {
  lat: string;
  long: string;
  radius: string;
  count: string;
};

export type NodeFetchDto = {
  issueId: string;
  itemId: string;
  nodeId: string;
  type: IIDRequestIdentifierNodeType;
};

export type AddressGeocodeResult = {
  place_id?: number;
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
  type?: string;
  address?: {
    house_number?: string;
    road?: string;
    pedestrian?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type AddressGeocodeResponse = {
  data: AddressGeocodeResult[];
};

export const MAX_COUNT = 100;

type MunicipalityDto = {
  id: string;
  name: string;
};

@Injectable({
  providedIn: 'root'
})
@InterfaceDataService(InterfaceName.IID_FEASABILITY_REQUEST_IDENTIFIER)
export class StudioFattibilitaRequestService extends CrudService<
  IID_FEASABILITY_REQUEST_IDENTIFIER,
  StudioFattibilitaRequestStore,
  StudioFattibilitaRequestQuery
> {

  private _particles$: Observable<DefaultOption[]>;
  private _municipalities$: Observable<DefaultOption[]>;

  constructor(
    protected store: StudioFattibilitaRequestStore,
    public query: StudioFattibilitaRequestQuery,
    protected actionService: ActionService,
    protected updateStrategy: ProjectEntityStoreStrategy,
    protected _http: HttpClient,
    private _translateService: TranslateService,
    private _feasabilityResponseService: IIDFeasabilityResponseService
  ) {
    super(Topics.IID_FEASABILITY_REQUEST_IDENTIFIER, store, query, actionService, updateStrategy);

    this._particles$ = this.fetchToponomasticParticles().pipe(
      map((parts) => parts.map((p) => ({ label: p, value: p }))),
      tap((opt) => console.debug('partics ', opt))
    );

    this._municipalities$ = this.fetchMunicipalities().pipe(
      map((muns) => muns.map((m) => ({ label: m.name, value: m.id }))),
      tap((opt) => console.debug('municip ', opt))
    );
  }

  get particles$(): Observable<DefaultOption[]> {
    return this._particles$.pipe(
      shareReplay({refCount: true, bufferSize: 1})
    );
  }

  get municipalities$(): Observable<DefaultOption[]> {
    return this._municipalities$.pipe(
      shareReplay({refCount: true, bufferSize: 1})
    );
  }

  patchDefaultP2P(item: IID_FEASABILITY_REQUEST_IDENTIFIER, isDoubleRoute: boolean): IID_FEASABILITY_REQUEST_IDENTIFIER {
    const secondNodePop = item?.nodes?.at(1);
    if (!secondNodePop || secondNodePop?.type !== 'pop') {
      return item;
    }
    const popIndex = item?.pops?.findIndex((p) => p.id === secondNodePop?.id);
    let pops = [...item?.pops];
    const copyPop = pops[popIndex];
    const bindingPops = item?.buildings?.at(0)?.linkPopIds?.length || 0;
    pops[popIndex] = { ...copyPop, secondRoute: this._setP2PSecondRoute(isDoubleRoute, bindingPops, popIndex) };
    if (isDoubleRoute && bindingPops === 0) {
      const copySecondPop = pops[popIndex + 1];
      pops[popIndex + 1] = { ...copySecondPop, secondRoute: this._setP2PSecondRoute(isDoubleRoute, bindingPops, popIndex + 1) };
    }
    return { ...item, pops };
  }

  patchDefaultL2L(item: IID_FEASABILITY_REQUEST_IDENTIFIER, isDoubleRoute: boolean): IID_FEASABILITY_REQUEST_IDENTIFIER {
    if (!isDoubleRoute) return item;
    const firstPop = item?.pops?.at(0);
    const secondPop = item?.pops?.at(1);
    let workingCopy = { ...item };
    if (firstPop) {
      const firstCopy = { ...firstPop, secondRoute: false };
      workingCopy.pops[0] = firstCopy;
    }
    if (secondPop) {
      const secondCopy = { ...secondPop, secondRoute: true };
      workingCopy.pops[1] = secondCopy;
    }
    return workingCopy;
  }

  patchDefaultDarkFiber(item: IID_FEASABILITY_REQUEST_IDENTIFIER): IID_FEASABILITY_REQUEST_IDENTIFIER {
    const thirdNode = item?.nodes?.at(2);
    if (!thirdNode || thirdNode.type !== 'pop') return item;

    const pops = (item?.pops || []).map((pop) =>
      pop.id === thirdNode.id ? { ...pop, secondRoute: true } : pop
    );

    return { ...item, pops };
  }

  private _setP2PSecondRoute(isSecondRoute: boolean, bindingPopCount: number, popIndex: number): boolean {
    if (!isSecondRoute) return false;
    if (bindingPopCount === 0) return popIndex !== 0; // primo pop false, secondo true
    if (bindingPopCount === 1) return popIndex === 0; // primo pop true
  }

  async isPointInsideMunicipality(dto: PointInsideMunicipalityDto): Promise<boolean> {
    try {
      return await this._http
        .post<boolean>(`${environment.apiUrl}/api/iid_feasability_request_identifier/feasibility/tool/isPointInsideMunicipality`, dto)
        .toPromise();
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async fetchBuildings(dto: BuildingParamsDto): Promise<IIDRequestIdentifierBuilding[]> {
    try {
      return await this._http
        .post<IIDRequestIdentifierBuilding[]>(`${environment.apiUrl}/api/iid_feasability_request_identifier/buildings/search`, dto)
        .toPromise();
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async geocodeAddress(comune: string, indirizzo: string, limit = 10): Promise<AddressGeocodeResult[]> {
    const response = await this._http
      .get<AddressGeocodeResponse>(`${environment.apiUrl}/api/iid_feasability_request_identifier/buildings/geocode`, {
        params: {
          comune,
          indirizzo,
          limit: String(limit),
        },
      })
      .pipe(timeout(15000))
      .toPromise();

    return response?.data || [];
  }

  async fetchPops(dto: PopsParamsDto): Promise<IIDRequestIdentifierPop[]> {
    try {
      return await this._http
        .post<IIDRequestIdentifierPop[]>(`${environment.apiUrl}/api/iid_feasability_request_identifier/pops/search`, dto)
        .toPromise();
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async fetchSplices(dto: SpliceSearchDto): Promise<IIDRequestIdentifierSplice[]> {
    try {
      return await this._http
        .get<IIDRequestIdentifierSplice[]>(`${environment.apiUrl}/api/iid_feasability_request_identifier/splices/search/${dto.lat}/${dto.long}/${dto.radius}/${dto.count}`)
        .toPromise();
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async sendFeasabilityStudy(dto: IID_FEASABILITY_REQUEST_IDENTIFIER): Promise<any> {
    try {
      const response = await this._http
        .post<any>(`${environment.apiUrl}/api/iid_feasability_request_identifier/feasibilityProjects`, dto)
        .toPromise();

      const message = typeof response === 'string' ? response : response?.message;

      if (dto?.id) {
        this.update({ ...dto, sent: true }, true);
      }
      this.clearLocalFeasabilityResponseError(dto);

      return message;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private clearLocalFeasabilityResponseError(dto: IID_FEASABILITY_REQUEST_IDENTIFIER): void {
    if (dto.cardinality === null || dto.cardinality === undefined) {
      return;
    }

    const cardinality = String(dto.cardinality);
    const responses = this._feasabilityResponseService.query.getByIssueId(dto.issueId)
      .filter((response: IID_FEASABILITY_RESPONSE) => {
        const responseCardinality = response.cardinality !== null && response.cardinality !== undefined
          ? String(response.cardinality)
          : response.unimarketId?.split("_")?.at(-1);

        return responseCardinality === cardinality && !!response.errorMessage;
      });

    responses.forEach((response) => {
      this._feasabilityResponseService.update({ ...response, errorMessage: null }, true);
    });
  }

  async checkPointInsideMunicipality(dto: PointInsideMunicipalityDto): Promise<boolean | null> {
    try {
      return await this._http
        .post<boolean>(`${environment.apiUrl}/api/iid_feasability_request_identifier/feasibility/tool/isPointInsideMunicipality`, dto)
        .toPromise();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  fetchToponomasticParticles(): Observable<string[]> {
    return this._http.get<string[]>(`${environment.apiUrl}/api/iid_feasability_request_identifier/feasibility/tool/toponomasticParticles`)
      .pipe(debounceTime(500),
        catchError(err => {
          console.error(err);
          return [];
        })
    );
  }

  fetchMunicipalities(): Observable<MunicipalityDto[]> {
    return this._http.get<MunicipalityDto[]>(`${environment.apiUrl}/api/iid_feasability_request_identifier/feasibility/tool/municipalities`)
      .pipe(debounceTime(500),
        catchError(err => {
          console.error(err);
          return [];
        })
    );
  }

  _getYesNoLabel(value?: boolean): string {
    return this._translateService.instant(`COMMON_WORDS.${!!value ? 'YES' : 'NO'}`);
  }
}
